import { useState, useEffect } from 'react'

import { t } from 'i18next'

import type { TProduto } from '@/features/produtos/interfaces/produto.types'
import type { TVagasSelecionadas } from '@/pages/proposta/etapa-informacoes-iniciais/vaga-padrao/vaga-padrao-trigger'
import { Icon } from '@/shared/components/icon'
import { ProdutoCard } from '@/shared/components/produto-card'
import { Render } from '@/shared/components/render-if'
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle, Button } from '@/shared/components/ui'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion'

type TVagaPadraoSelectorProps = {
  vagas: Array<{ nome: string; produtos: TProduto[] }>
  defaultSelecionadas: TVagasSelecionadas
  onConfirm: (selected: TVagasSelecionadas) => void
  handleGoBack?: () => void
}

export const VagaPadraoSelector = ({
  vagas,
  defaultSelecionadas,
  onConfirm,
  handleGoBack,
}: TVagaPadraoSelectorProps) => {
  const [vagasSelecionadas, setVagasSelecionadas] = useState<TVagasSelecionadas>(defaultSelecionadas)

  const toggleProduto = (acessorio: TProduto, nomeVaga: string) => {
    setVagasSelecionadas((prev) =>
      prev.some((item) => item.id === acessorio.idExterno)
        ? prev.filter((item) => item.id !== acessorio.idExterno)
        : [...prev, { nomeVaga, id: acessorio.idExterno, descricao: acessorio.descricao }],
    )
  }

  const vagasSelecionadasIds = new Set(vagasSelecionadas.map((vaga) => vaga.id))

  useEffect(() => {
    setVagasSelecionadas(defaultSelecionadas)
  }, [defaultSelecionadas])

  return (
    <>
      <SheetHeader
        data-voltar={!!handleGoBack}
        className="box-border max-w-137 gap-2 p-0 pt-16 data-[voltar=true]:pt-9"
      >
        <Render.If condition={!!handleGoBack}>
          {() => (
            <Button
              onClick={handleGoBack}
              variant="ghost"
              className="fit-content text-primary hover:text-primary/70 flex w-fit p-0 hover:bg-transparent [&_svg]:size-6"
            >
              <Icon name="arrow-left" />
              <p className="hidden text-base font-medium sm:block">{t('common:voltar')}</p>
            </Button>
          )}
        </Render.If>

        <SheetTitle className="text-2xl font-normal uppercase">
          {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.titulo')}
        </SheetTitle>
        <SheetDescription className="text-base text-gray-500">
          {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.descricao')}
        </SheetDescription>
        {!!vagasSelecionadas.length && (
          <p className="mt-2 text-sm text-gray-500">
            {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.vagas_selecionadas', {
              quantidade: vagasSelecionadas.length,
            })}
          </p>
        )}
      </SheetHeader>

      <Accordion
        type="multiple"
        className="w-full space-y-4 overflow-auto"
        defaultValue={defaultSelecionadas.map((vaga) => vaga.nomeVaga)}
      >
        {vagas.map((vaga) => (
          <AccordionItem value={vaga.nome} key={vaga.nome} className="rounded-md border px-4 py-1 shadow-sm">
            <AccordionTrigger className="py-2 font-medium">
              <div className="capitalize">{vaga.nome.toLocaleLowerCase()}</div>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4 sm:p-1.5">
              {vaga.produtos.map((acessorio) => (
                <ProdutoCard
                  key={acessorio.id}
                  produto={acessorio}
                  nomeAgrupador={vaga.nome}
                  disabled={acessorio.status !== 'Disponivel'}
                  isSelected={vagasSelecionadasIds.has(acessorio.idExterno)}
                  className="cursor-pointer disabled:cursor-not-allowed data-[selected=true]:border-2 data-[selected=true]:border-teal-600"
                  handleSelecionarProduto={() => toggleProduto(acessorio, vaga.nome)}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <SheetFooter className="fixed right-0 bottom-0 left-0 bg-white p-4 shadow-[0_-2px_10px_0_rgba(0,0,0,0.1)] sm:relative sm:grid sm:p-0 sm:py-4 sm:shadow-none">
        <Button
          className="w-fit w-full justify-self-end aria-[disabled=true]:pointer-events-none sm:w-fit"
          onClick={() => onConfirm(vagasSelecionadas)}
        >
          {t('proposta:informacoes_iniciais.cards.vaga_padrao.trigger.confirmar')}
        </Button>
      </SheetFooter>
    </>
  )
}
